import React, { useState, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, CheckCircle2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE = 'bg_customer_auth';

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const missingToken = useMemo(() => !token, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (pw.length < 6) return setErr('Password must be at least 6 characters.');
    if (pw !== confirm) return setErr('Passwords do not match.');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: pw,
      });
      // Persist session so user is signed in immediately.
      if (data?.token && data?.email) {
        localStorage.setItem(STORAGE, JSON.stringify({ token: data.token, email: data.email }));
      }
      setDone(true);
      // Send them home after a short beat.
      setTimeout(() => navigate('/', { replace: true }), 1600);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="reset-password-page"
      className="min-h-screen bg-[#0b0b0c] text-neutral-100 flex items-center justify-center px-4 py-16"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="font-serif text-[28px] tracking-wide text-neutral-100">Healsglobe Exports</div>
          <div className="text-[10.5px] tracking-[0.22em] uppercase text-neutral-500 mt-1">
            Pharma Catalog
          </div>
        </div>

        <div className="bg-[#141416] border border-neutral-800 rounded-2xl p-6">
          <h1 className="font-serif text-[22px] mb-1">Set a new password</h1>
          <p className="text-[13px] text-neutral-400 mb-5 leading-relaxed">
            Enter a new password below. You'll be signed in automatically after resetting.
          </p>

          {missingToken ? (
            <div
              data-testid="reset-password-missing-token"
              className="flex items-start gap-3 rounded-md bg-red-950/30 border border-red-900 p-4"
            >
              <AlertCircle size={18} className="text-red-400 mt-0.5 shrink-0" />
              <div className="text-[13px] text-red-200 leading-relaxed">
                This reset link is missing a token. Please request a new reset email from the sign-in
                screen.
              </div>
            </div>
          ) : done ? (
            <div
              data-testid="reset-password-success"
              className="flex items-start gap-3 rounded-md bg-emerald-950/30 border border-emerald-900 p-4"
            >
              <CheckCircle2 size={18} className="text-emerald-400 mt-0.5 shrink-0" />
              <div className="text-[13px] text-emerald-200 leading-relaxed">
                Password updated. Redirecting you to the shop…
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-[10.5px] tracking-[0.12em] text-neutral-500 uppercase mb-1.5">
                  New password
                </label>
                <div className="relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                  <input
                    data-testid="reset-password-new-input"
                    type="password"
                    required
                    minLength={6}
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="At least 6 characters"
                    className="w-full bg-[#0f0f10] border border-neutral-800 rounded-md pl-9 pr-3 py-2.5 text-[14px] outline-none focus:border-neutral-600 text-neutral-100"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10.5px] tracking-[0.12em] text-neutral-500 uppercase mb-1.5">
                  Confirm password
                </label>
                <div className="relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                  <input
                    data-testid="reset-password-confirm-input"
                    type="password"
                    required
                    minLength={6}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repeat your new password"
                    className="w-full bg-[#0f0f10] border border-neutral-800 rounded-md pl-9 pr-3 py-2.5 text-[14px] outline-none focus:border-neutral-600 text-neutral-100"
                  />
                </div>
              </div>

              {err && (
                <p className="text-red-400 text-[13px]" data-testid="reset-password-error">{err}</p>
              )}

              <button
                data-testid="reset-password-submit"
                type="submit"
                disabled={loading}
                className={`w-full rounded-lg py-3 text-[14.5px] font-medium transition-colors ${loading ? 'bg-neutral-700 text-neutral-400 cursor-not-allowed' : 'bg-emerald-700 hover:bg-emerald-600 text-white'}`}
              >
                {loading ? 'Updating…' : 'Update password'}
              </button>
            </form>
          )}

          <div className="mt-6 pt-4 border-t border-neutral-800 text-center">
            <Link
              to="/"
              data-testid="reset-password-back-home"
              className="text-[12.5px] text-neutral-400 hover:text-neutral-200 underline underline-offset-2"
            >
              Back to shop
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
